import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/compat/router';
import _ from 'lodash';
import Fuse from 'fuse.js';

import usePaths from 'hooks/use-paths';
import useProductQueryParams from 'hooks/use-product-query-params';

import { ParsedUrlQuery } from 'querystring';
import { tracker } from 'src/utils/analytics';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Brands } from './brands-filter.types';

type Brand = {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
  href: string;
};

type ParsedBrand = {
  key: string;
  label: string;
  value: string;
};

type ParsedBrands = ParsedBrand[];

type BrandsFilterReturn = {
  brandsFromQueryParams: string[];
  brandOptionsToRender: () => ParsedBrand[];
  defaults: any;
  handleBrandSelection: (brand: string) => void;
  href: string;
  onBrandsPage: boolean;
  query: ParsedUrlQuery;
  route: string;
  filteredBrands: ParsedBrands;
  setSearch: (search: string) => void;
  search: string;
  setExpanded: (expanded: boolean) => void;
  expanded: boolean;
  displaySearchBar: boolean;
  displayViewMore: boolean;
  convertedBrands: Brand[];
  handleBrandClickEvent: (brand: string) => void;
};

type Option = {
  key: string;
  value: string;
  label: string;
  queryValue?: string;
};

export function useBrandsFilter(brands: Brands): BrandsFilterReturn {
  const { query, route: brandRoute } = useRouter() ?? {};
  const flags = useFlags();

  const { href, route } = usePaths({ brands: true });
  const { defaults, queryParams, setQueryParams } = useProductQueryParams();
  const [search, setSearch] = useState('');

  const consolidatedBrands = _.uniqBy(brands, `name`);
  const parsedBrands = consolidatedBrands.map(({ id, name }) => ({ key: id, value: _.kebabCase(name), label: name }));

  const pinnedBrandIds = flags['growth.ecomm.pinned-brands.entitlement'] ?? [];
  const pinnedBrands = parsedBrands.filter((brand) => pinnedBrandIds.includes(brand.key));

  // Create Fuse instance with options
  const fuseInstance = useMemo(() => {
    const options = {
      keys: ['value', 'label'], // Search both the kebab-case value and original name
      threshold: 0.3, // Lower threshold = more strict matching (0.0 - 1.0)
      ignoreLocation: true,
    };
    return new Fuse(parsedBrands, options);
  }, [parsedBrands]);

  const filteredBrands = useMemo(() => {
    if (!search) {
      return parsedBrands;
    }
    return fuseInstance.search(search).map((result) => result.item);
  }, [parsedBrands, search, fuseInstance]);

  const brandsFromQueryParams = queryParams.brands;

  const setupViewMoreExpanded = (options: Option[], selectedOptions: string[] | null): boolean => {
    const optionsPastSix = options.slice(6);
    return optionsPastSix.some((option) => selectedOptions?.includes(option.queryValue ?? option.value));
  };

  const initiallyExpanded = useMemo(() => setupViewMoreExpanded(filteredBrands, brandsFromQueryParams), [
    filteredBrands,
    brandsFromQueryParams,
  ]);
  const [expanded, setExpanded] = useState(initiallyExpanded);

  const onBrandsPage = _.includes(brandRoute, 'brands');

  const handleBrandClickEvent = (brand: string): void => {
    tracker.gaGTMClickEvent({
      eventLabel: `brands > ${brand}`,
      eventCategory: `navigation click`,
      eventAction: `main menu`,
    });
  };

  const handleBrandSelection = useCallback(
    (brand: string) => {
      if (onBrandsPage) {
        handleBrandClickEvent(brand);
      }

      // If brandsInQuery contains brand, remove it
      // If brandsInQuery doesn't contain brand, add it
      const newBrands = _.xor(brandsFromQueryParams, [brand]);

      setQueryParams({
        brands: newBrands,
        page: defaults.page,
      });
      setExpanded(false);
    },
    [brandsFromQueryParams, defaults.page, onBrandsPage, setQueryParams]
  );

  const sortBrandsWithPinnedFirst = (): ParsedBrand[] =>
    [...filteredBrands].sort((a, b) => {
      const aPinned = pinnedBrands.some((brand) => brand.key === a.key);
      const bPinned = pinnedBrands.some((brand) => brand.key === b.key);

      if (aPinned && !bPinned) {
        return -1;
      }
      if (!aPinned && bPinned) {
        return 1;
      }
      if (aPinned && bPinned) {
        // Both are pinned, use the order from pinnedBrandIds
        return pinnedBrandIds.indexOf(a.key) - pinnedBrandIds.indexOf(b.key);
      }
      return 0;
    });

  const brandOptionsToRender = (): ParsedBrand[] => {
    const selectedBrands = onBrandsPage
      ? parsedBrands.filter((brand) => brand.value === query?.brandId)
      : parsedBrands.filter((brand) => brandsFromQueryParams?.includes(brand.value));

    const sortedFilteredBrands = sortBrandsWithPinnedFirst();
    const finalList = _.uniqBy([...selectedBrands, ...sortedFilteredBrands], 'value');

    // Sort to place selected brands at the top (alphabetically)
    finalList.sort((a, b) => {
      const aSelected = selectedBrands.some((brand) => brand.value === a.value);
      const bSelected = selectedBrands.some((brand) => brand.value === b.value);

      // If both are selected, sort alphabetically by label
      if (aSelected && bSelected) {
        return a.label.localeCompare(b.label);
      }

      // If only one is selected, it comes first
      if (aSelected) {
        return -1;
      }
      if (bSelected) {
        return 1;
      }

      return 0;
    });

    return finalList;
  };

  const displaySearchBar = parsedBrands.length > 6;
  const displayViewMore = filteredBrands.length > 6;

  const getBrandHref = (brandName: string): string => {
    const brandSlug = _.kebabCase(brandName);
    return `${href}/${brandSlug}`;
  };

  const convertedBrands = sortBrandsWithPinnedFirst()
    .map((parsedBrand) => {
      const originalBrand = brands.find((brand) => brand.id === parsedBrand.key);

      // Skip brands that don't exist in the original brands array
      if (!originalBrand) {
        return null;
      }

      return {
        id: parsedBrand.key,
        name: parsedBrand.label,
        imageUrl: originalBrand.imageUrl ?? undefined,
        description: originalBrand.description ?? undefined,
        href: getBrandHref(originalBrand.name),
      };
    })
    .filter(Boolean) as Brand[];

  return {
    brandsFromQueryParams,
    brandOptionsToRender,
    defaults,
    handleBrandSelection,
    href,
    onBrandsPage,
    query: query ?? {},
    route,
    filteredBrands,
    setSearch,
    search,
    setExpanded,
    expanded,
    displaySearchBar,
    displayViewMore,
    convertedBrands,
    handleBrandClickEvent,
  };
}
